<template>
  <div class="jim-scheduling-container">
    <div class="forecast-page">
      <h1 class="page-heading">Purchasing Forecast</h1>

      <div class="forecast-layout">
        <!-- ==================================================
             LEFT-SIDE CONTROLS
        =================================================== -->
        <div class="left-side-section">
          <button
            class="plan-it-btn"
            @click="loadControl"
            :disabled="loading || saving"
          >
            {{ loading ? "Loading…" : "Reload Controls" }}
          </button>

          <button
            class="plan-it-btn"
            @click="previewForecast"
            :disabled="loading || saving"
          >
            {{ loading ? "Calculating…" : "Preview Forecast" }}
          </button>

          <button
            class="plan-it-btn save-btn"
            @click="saveForecast"
            :disabled="
                !previewLoaded ||
                previewStale ||
                loading ||
                saving
            "
          >
            {{ saving ? "Saving…" : "Save & Activate" }}
          </button>

          <button
            class="plan-it-btn"
            @click="loadActiveForecast"
            :disabled="loading || saving"
          >
            Load Active Forecast
          </button>

          <button
            class="plan-it-btn"
            @click="showPeriodMultiplierModal = true"
            :disabled="loading || saving"
            >
            Period Multipliers
          </button>
          
          <button
            class="plan-it-btn"
            @click="resetMultipliers"
            :disabled="loading || saving"
          >
            Reset Multipliers
          </button>

          <div class="side-help">
            <div>
              <strong>History:</strong>
              Two complete prior months
            </div>

            <div>
              <strong>Fallback:</strong>
              Prior-year usage
            </div>

            <div>
              <strong>Locations:</strong>
              F, D, K, L
            </div>

            <div>
              <strong>Overrides:</strong>
              Require a reason
            </div>

            <div>
              <strong>Forecast:</strong>
              Saved snapshot
            </div>
          </div>

          <div
            v-if="activeForecast"
            class="active-forecast-box"
          >
            <div class="box-title">Active Forecast</div>

            <div>
              {{ activeForecast.forecast_name }}
            </div>

            <div class="small-muted">
              Month:
              {{ formatDate(activeForecast.current_month) }}
            </div>

            <div class="small-muted">
              Created:
              {{ formatDateTime(activeForecast.created_at) }}
            </div>

            <div class="small-muted">
              By:
              {{ activeForecast.created_by || "Unknown" }}
            </div>
          </div>

          <div
            v-if="summary.flaggedRows"
            class="warning-box"
          >
            <strong>Flagged:</strong>
            {{ formatNumber(filteredSummary.flaggedRows, 0) }}
            item/location rows
          </div>

          <div
            v-if="summary.overriddenRows"
            class="override-box"
          >
            <strong>Overrides:</strong>
            {{ formatNumber(summary.overriddenRows, 0) }}
            item/location rows
          </div>

          <div
            v-if="previewLoaded && previewStale"
            class="stale-preview-warning"
            >
            Period multipliers have changed. Click
            <strong>Preview Forecast</strong>
            to recalculate the forecast before saving.
         </div>
        </div>

        

        <!-- ==================================================
             MAIN PAGE
        =================================================== -->
        <div class="forecast-wrapper">
          <!-- ==================================================
               CONTROL PANEL
          =================================================== -->
          <div class="control-panel">
            <div class="control-grid">
              <div class="control-field">
                <label for="forecastName">
                  Forecast Name
                </label>

                <input
                  id="forecastName"
                  v-model.trim="forecastName"
                  type="text"
                  placeholder="Commodity Forecast July 2026"
                />
              </div>

              <div class="control-field">
                <label for="currentMonth">
                  Month 1
                </label>

                <input
                  id="currentMonth"
                  v-model="currentMonth"
                  type="month"
                  @change="handleMonthChange"
                />
              </div>

              <div class="control-field">
                <label for="deviationThreshold">
                  Review Threshold %
                </label>

                <input
                  id="deviationThreshold"
                  v-model.number="deviationThresholdPct"
                  type="number"
                  min="0"
                  max="1000"
                  step="1"
                />
              </div>

              <div class="control-field notes-field">
                <label for="forecastNotes">
                  Forecast Notes
                </label>

                <input
                  id="forecastNotes"
                  v-model.trim="notes"
                  type="text"
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <div
              v-if="control"
              class="date-summary-grid"
            >
              <div class="date-card">
                <div class="date-label">
                  History Start
                </div>

                <div class="date-value">
                  {{ formatDate(control.historyStart) }}
                </div>
              </div>

              <div class="date-card">
                <div class="date-label">
                  History End
                </div>

                <div class="date-value">
                  {{ formatDate(control.historyEnd) }}
                </div>
              </div>

              <div class="date-card">
                <div class="date-label">
                  History Workdays
                </div>

                <div class="date-value">
                  {{ control.historyWorkdays }}
                </div>
              </div>

              <div class="date-card">
                <div class="date-label">
                  Prior Year
                </div>

                <div class="date-value">
                  {{ formatDate(control.priorYearStart) }}
                  –
                  {{ formatDate(control.priorYearEnd) }}
                </div>
              </div>

              <div class="date-card">
                <div class="date-label">
                  Prior-Year Workdays
                </div>

                <div class="date-value">
                  {{ control.priorYearWorkdays }}
                </div>
              </div>
            </div>
          </div>

          
          <!-- ==================================================
               SUMMARY BAR
          =================================================== -->
          <div
            v-if="previewLoaded"
            class="summary-bar"
          >
            <div class="summary-item">
              <div class="summary-label">
                Raw Materials
              </div>

              <div class="summary-value">
                {{ formatNumber(filteredSummary.rawMaterialItems, 0) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Item/Locations
              </div>

              <div class="summary-value">
                {{ formatNumber(filteredSummary.itemLocationRows, 0) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Flagged
              </div>

              <div class="summary-value warning-value">
                {{ formatNumber(filteredSummary.flaggedRows, 0) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Overridden
              </div>

              <div class="summary-value override-value">
                {{ formatNumber(filteredSummary.overriddenRows, 0) }}
              </div>
            </div>

            
          </div>

          <!-- ==================================================
               FILTERS
          =================================================== -->
          <div
            v-if="previewLoaded"
            class="filter-panel"
          >
            <div class="filter-field search-field">
              <label for="searchText">
                Search Item
              </label>

              <input
                id="searchText"
                v-model.trim="searchText"
                type="text"
                placeholder="Item number"
              />
            </div>

            <div class="filter-field">
              <label for="locationFilter">
                Location
              </label>

              <select
                id="locationFilter"
                v-model="locationFilter"
              >
                <option value="">
                  All
                </option>

                <option
                  v-for="loc in locations"
                  :key="loc"
                  :value="loc"
                >
                  {{ locationName(loc) }}
                </option>
              </select>
            </div>

            <div class="filter-check">
              <label>
                <input
                  v-model="flaggedOnly"
                  type="checkbox"
                />

                Flagged only
              </label>
            </div>

            <div class="filter-check">
              <label>
                <input
                  v-model="overridesOnly"
                  type="checkbox"
                />

                Overrides only
              </label>
            </div>

            <div class="filter-count">
              Showing
              {{ filteredRows.length }}
              of
              {{ rows.length }}
            </div>
          </div>

          <!-- ==================================================
               FORECAST DETAIL TABLE
          =================================================== -->
          <div
            v-if="previewLoaded"
            class="table-panel"
          >
            <div class="table-header-row">
              <div>
                <div class="panel-title">
                  Forecast Detail
                </div>

                <div class="small-muted">
                  Yellow override fields replace the calculated monthly quantity.
                  A reason is required before saving.
                </div>
              </div>
            </div>

            <div class="table-scroll detail-scroll">
              <table class="forecast-table detail-table">
                <thead>
                  <tr>
                    <th rowspan="2">Item</th>
                    <th rowspan="2">Loc</th>
                    <th rowspan="2">Basis</th>
                    <th rowspan="2">Recent Usage</th>
                    <th rowspan="2">Prior Year</th>
                    <th rowspan="2">Recent Daily</th>
                    <th rowspan="2">Annual Daily</th>
                    <th rowspan="2">Deviation</th>
                    <th rowspan="2">Flags</th>

                    <th colspan="3">
                      {{ monthLabel(1) }}
                    </th>

                    <th colspan="3">
                      {{ monthLabel(2) }}
                    </th>

                    <th colspan="3">
                      {{ monthLabel(3) }}
                    </th>

                    <th rowspan="2">Avg Daily</th>
                    <th rowspan="2">Avg Month</th>
                    <th rowspan="2">Override Reason</th>
                  </tr>

                  <tr>
                    <th>Calc</th>
                    <th>Override</th>
                    <th>Final</th>

                    <th>Calc</th>
                    <th>Override</th>
                    <th>Final</th>

                    <th>Calc</th>
                    <th>Override</th>
                    <th>Final</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="row in filteredRows"
                    :key="rowKey(row)"
                    :class="rowClasses(row)"
                  >
                    <td class="item-cell">
                      {{ row.itemNo }}
                    </td>

                    <td class="location-cell">
                      {{ row.loc }}
                    </td>

                    <td>
                      {{ basisLabel(row.forecastBasis) }}
                    </td>

                    <td>
                      {{ formatNumber(row.historyUsage, 4) }}
                    </td>

                    <td>
                      {{ formatNumber(row.priorYearUsage, 4) }}
                    </td>

                    <td>
                      {{ formatNumber(row.recentDailyUsage, 4) }}
                    </td>

                    <td>
                      {{ formatNumber(row.annualDailyUsage, 4) }}
                    </td>

                    <td :class="deviationClass(row.deviationPct)">
                      {{ formatPercent(row.deviationPct) }}
                    </td>

                    <td class="flags-cell">
                      <span
                        v-for="flag in row.reviewFlags"
                        :key="flag"
                        class="flag-pill"
                        :class="flagClass(flag)"
                      >
                        {{ flagLabel(flag) }}
                      </span>

                      <span
                        v-if="!row.reviewFlags.length"
                        class="small-muted"
                      >
                        —
                      </span>
                    </td>

                    <!-- MONTH 1 -->
                    <td>
                      {{ formatNumber(row.month1.calculatedQty, 2) }}
                    </td>

                    <td class="override-cell">
                      <input
                        v-model="row.month1.overrideQty"
                        class="override-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="—"
                        @input="handleOverrideChange(row)"
                      />
                    </td>

                    <td class="final-cell">
                      {{ formatNumber(row.month1.finalQty, 2) }}
                    </td>

                    <!-- MONTH 2 -->
                    <td>
                      {{ formatNumber(row.month2.calculatedQty, 2) }}
                    </td>

                    <td class="override-cell">
                      <input
                        v-model="row.month2.overrideQty"
                        class="override-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="—"
                        @input="handleOverrideChange(row)"
                      />
                    </td>

                    <td class="final-cell">
                      {{ formatNumber(row.month2.finalQty, 2) }}
                    </td>

                    <!-- MONTH 3 -->
                    <td>
                      {{ formatNumber(row.month3.calculatedQty, 2) }}
                    </td>

                    <td class="override-cell">
                      <input
                        v-model="row.month3.overrideQty"
                        class="override-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="—"
                        @input="handleOverrideChange(row)"
                      />
                    </td>

                    <td class="final-cell">
                      {{ formatNumber(row.month3.finalQty, 2) }}
                    </td>

                    <td>
                      {{ formatNumber(row.averageDaily, 4) }}
                    </td>

                    <td>
                      {{ formatNumber(row.averageMonthly, 2) }}
                    </td>

                    <td class="reason-cell">
                      <input
                        v-model.trim="row.overrideReason"
                        class="reason-input"
                        type="text"
                        :disabled="!row.hasOverride"
                        :class="{
                          'missing-reason':
                            row.hasOverride &&
                            !String(row.overrideReason || '').trim()
                        }"
                        placeholder="Required for override"
                      />
                    </td>
                  </tr>

                  <tr v-if="filteredRows.length === 0">
                    <td
                      colspan="21"
                      class="empty-row"
                    >
                      No forecast rows match the current filters.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Period Multipliers Modal -->
        <div
        v-if="showPeriodMultiplierModal"
        class="shortage-modal-backdrop"
        @click.self="closePeriodMultiplierModal"
        >
        <div class="shortage-modal multiplier-modal">
            <div class="shortage-modal-header">
            <div>
                <h3>Period Multipliers</h3>

                <div class="small-muted">
                Enter 1.10 for a 10% increase or 0.90 for a 10% decrease.
                </div>
            </div>

            <button
                class="close-btn"
                type="button"
                @click.stop="closePeriodMultiplierModal"
            >
                ✕
            </button>
            </div>

            <div class="shortage-modal-body">
            <div class="multiplier-modal-actions">
                <button
                    class="small-btn"
                    type="button"
                    @click.stop="copyMonthOneAcross"
                    :disabled="saving"
                >
                    Copy Month 1 Across
                </button>

                <button
                    class="small-btn"
                    type="button"
                    @click.stop="resetMultipliers"
                    :disabled="saving"
                >
                    Reset All to 1.00
                </button>
            </div>

            <div class="multiplier-modal-note">
                Changes affect the next forecast preview. Existing saved forecasts are
                not changed until a new forecast is saved and activated.
            </div>

            <div class="table-scroll multiplier-scroll">
                <table class="forecast-table multiplier-table">
                <thead>
                    <tr>
                    <th>Period</th>

                    <th
                        v-for="loc in locations"
                        :key="loc"
                    >
                        {{ locationName(loc) }}
                    </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                    v-for="period in periods"
                    :key="period.period"
                    >
                    <td class="period-cell">
                        <div>
                        Month {{ period.period }}
                        </div>

                        <div class="small-muted">
                        {{ period.label }}
                        </div>

                        <div class="small-muted">
                        {{ period.workdays }} workdays
                        </div>
                    </td>

                    <td
                        v-for="loc in locations"
                        :key="`${loc}-${period.period}`"
                    >
                        <input
                        v-model.number="
                            multipliers[loc][period.period - 1]
                        "
                        class="multiplier-input"
                        type="number"
                        min="0"
                        max="10"
                        step="0.01"
                        @change="handleMultiplierChange"
                        />
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>

            <div class="multiplier-modal-footer">
                <button
                class="plan-it-btn"
                type="button"
                @click.stop="closePeriodMultiplierModal"
                >
                Done
                </button>
            </div>
            </div>
        </div>
        </div>

      <PabloChatSidebar :loc="selectedLocation" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import PabloChatSidebar from "./PabloChatSidebar.vue";

const LOCATIONS = ["F", "D", "K", "L"];

export default {
  name: "PurchasingForecast",

  components: {
    PabloChatSidebar
  },

  data() {
    return {
      loading: false,
      saving: false,

      control: null,
      activeForecast: null,

      forecastName: "",
      currentMonth: "",
      deviationThresholdPct: 50,
      notes: "",

      locations: [...LOCATIONS],

      showPeriodMultiplierModal: false,
     

      multipliers: {
        F: [1, 1, 1],
        D: [1, 1, 1],
        K: [1, 1, 1],
        L: [1, 1, 1]
      },

      rows: [],

      summary: {
        itemLocationRows: 0,
        rawMaterialItems: 0,
        flaggedRows: 0,
        overriddenRows: 0,
        month1Total: 0,
        month2Total: 0,
        month3Total: 0
      },

      searchText: "",
      locationFilter: "",
      flaggedOnly: false,
      overridesOnly: false,

      previewStale: false,

      previewLoaded: false
    };
  },

  computed: {
    selectedLocation() {
      return this.$root.selectedLocation;
    },

    periods() {
      return Array.isArray(this.control?.months)
        ? this.control.months
        : [
            {
              period: 1,
              label: "Month 1",
              workdays: 0
            },
            {
              period: 2,
              label: "Month 2",
              workdays: 0
            },
            {
              period: 3,
              label: "Month 3",
              workdays: 0
            }
          ];
    },

    filteredSummary() {
        const rows = this.filteredRows;

        const summary = {
            itemLocationRows: rows.length,

            rawMaterialItems: new Set(
            rows.map((row) => row.itemNo)
            ).size,

            flaggedRows: 0,
            overriddenRows: 0,

            month1Total: 0,
            month2Total: 0,
            month3Total: 0
        };

        for (const row of rows) {
            if (row.reviewFlags.length) {
            summary.flaggedRows += 1;
            }

            if (row.hasOverride) {
            summary.overriddenRows += 1;
            }

            summary.month1Total += Number(
            row.month1.finalQty || 0
            );

            summary.month2Total += Number(
            row.month2.finalQty || 0
            );

            summary.month3Total += Number(
            row.month3.finalQty || 0
            );
        }

        return summary;
    },

    filteredRows() {
      const search = String(this.searchText || "")
        .trim()
        .toUpperCase();

      return this.rows.filter((row) => {
        if (
          search &&
          !String(row.itemNo || "")
            .toUpperCase()
            .includes(search)
        ) {
          return false;
        }

        if (
          this.locationFilter &&
          row.loc !== this.locationFilter
        ) {
          return false;
        }

        if (
          this.flaggedOnly &&
          !row.reviewFlags.length
        ) {
          return false;
        }

        if (
          this.overridesOnly &&
          !row.hasOverride
        ) {
          return false;
        }

        return true;
      });
    }
  },

  methods: {
    rowKey(row) {
      return `${row.itemNo}-${row.loc}`;
    },

    locationName(loc) {
      const names = {
        F: "Fresno",
        D: "Denver",
        K: "Knoxville",
        L: "Lakeland"
      };

      return names[loc] || loc;
    },

    closePeriodMultiplierModal() {
        this.showPeriodMultiplierModal = false;
    },

    handleMultiplierChange() {
        this.previewStale = true;
    },

    monthLabel(periodNumber) {
      const period = this.periods.find(
        (item) => item.period === periodNumber
      );

      return period?.label || `Month ${periodNumber}`;
    },

    formatDate(value) {
      if (!value) return "—";

      const date = new Date(
        `${String(value).slice(0, 10)}T00:00:00`
      );

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString();
    },

    formatDateTime(value) {
      if (!value) return "—";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },

    formatNumber(value, digits = 2) {
      const number = Number(value || 0);

      return number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits
      });
    },

    formatPercent(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "—";
      }

      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "—";
      }

      return `${number.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}%`;
    },

    basisLabel(value) {
      const labels = {
        RECENT_HISTORY: "Recent",
        PRIOR_YEAR: "Prior Year",
        NO_USAGE: "No Usage"
      };

      return labels[value] || value || "—";
    },

    flagLabel(flag) {
        const labels = {
            RECENT_USAGE_HIGH: "Recent High",
            RECENT_USAGE_LOW: "Recent Low",
            NEGATIVE_RECENT_USAGE: "Negative Recent",
            MISSING_ANNUAL_COMPARISON: "No Annual Comparison",
            MANUAL_OVERRIDE: "Override"
        };

        return labels[flag] || flag;
    },

    flagClass(flag) {
      return {
        "flag-high": flag === "RECENT_USAGE_HIGH",
        "flag-low": flag === "RECENT_USAGE_LOW",
        "flag-negative": flag === "NEGATIVE_RECENT_USAGE",
        "flag-missing":
          flag === "MISSING_ANNUAL_COMPARISON",
        "flag-override": flag === "MANUAL_OVERRIDE"
      };
    },

    deviationClass(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "";
      }

      if (number >= this.deviationThresholdPct) {
        return "positive-deviation";
      }

      if (number <= -this.deviationThresholdPct) {
        return "negative-deviation";
      }

      return "";
    },

    rowClasses(row) {
      return {
        "flagged-row": row.reviewFlags.length > 0,
        "overridden-row": row.hasOverride
      };
    },

    normalizeMonthForInput(value) {
      if (!value) return "";

      return String(value).slice(0, 7);
    },

    monthInputToDate(value) {
      if (!value) return "";

      return `${value}-01`;
    },

    buildDefaultForecastName() {
      if (!this.currentMonth) {
        return "Commodity Forecast";
      }

      const date = new Date(
        `${this.currentMonth}-01T00:00:00`
      );

      return `Commodity Forecast ${date.toLocaleString(
        "en-US",
        {
          month: "long",
          year: "numeric"
        }
      )}`;
    },

    clearPreview() {
      this.previewLoaded = false;
      this.rows = [];
      this.resetSummary();
    },

    resetSummary() {
      this.summary = {
        itemLocationRows: 0,
        rawMaterialItems: 0,
        flaggedRows: 0,
        overriddenRows: 0,
        month1Total: 0,
        month2Total: 0,
        month3Total: 0
      };
    },

    normalizeSavedRow(row) {
        const reviewFlags = Array.isArray(row.review_flags)
            ? [...row.review_flags]
            : [];

        return {
            itemNo: row.item_no,
            loc: row.loc,

            historyUsage: Number(row.history_usage || 0),
            priorYearUsage: Number(row.prior_year_usage || 0),

            recentDailyUsage: Number(row.recent_daily_usage || 0),
            annualDailyUsage: Number(row.annual_daily_usage || 0),
            baseDailyUsage: Number(row.base_daily_usage || 0),

            forecastBasis: row.forecast_basis,

            deviationPct:
            row.deviation_pct === null ||
            row.deviation_pct === undefined
                ? null
                : Number(row.deviation_pct),

            reviewFlags,

            month1: {
            daily: Number(row.month_1_daily || 0),

            calculatedQty: Number(
                row.month_1_calculated_qty || 0
            ),

            overrideQty:
                row.month_1_override_qty === null ||
                row.month_1_override_qty === undefined
                ? ""
                : Number(row.month_1_override_qty),

            finalQty: Number(
                row.month_1_final_qty || 0
            )
            },

            month2: {
            daily: Number(row.month_2_daily || 0),

            calculatedQty: Number(
                row.month_2_calculated_qty || 0
            ),

            overrideQty:
                row.month_2_override_qty === null ||
                row.month_2_override_qty === undefined
                ? ""
                : Number(row.month_2_override_qty),

            finalQty: Number(
                row.month_2_final_qty || 0
            )
            },

            month3: {
            daily: Number(row.month_3_daily || 0),

            calculatedQty: Number(
                row.month_3_calculated_qty || 0
            ),

            overrideQty:
                row.month_3_override_qty === null ||
                row.month_3_override_qty === undefined
                ? ""
                : Number(row.month_3_override_qty),

            finalQty: Number(
                row.month_3_final_qty || 0
            )
            },

            averageDaily: Number(row.average_daily || 0),
            averageMonthly: Number(row.average_monthly || 0),

            overrideReason: row.override_reason || "",

            hasOverride:
            row.month_1_override_qty !== null &&
            row.month_1_override_qty !== undefined
                ? true
                : row.month_2_override_qty !== null &&
                row.month_2_override_qty !== undefined
                ? true
                : row.month_3_override_qty !== null &&
                row.month_3_override_qty !== undefined
        };
    },

    async loadActiveForecast() {
        this.loading = true;

        try {
            const { data } = await axios.get(
            "/purchasing-forecast/active"
            );

            const run = data?.run || null;

            const periods = Array.isArray(data?.periods)
            ? data.periods
            : [];

            const savedRows = Array.isArray(data?.rows)
            ? data.rows
            : [];

            if (!run) {
            throw new Error(
                "No active Purchasing forecast was returned."
            );
            }

            this.activeForecast = run;

            this.forecastName =
            run.forecast_name || "";

            this.currentMonth =
            this.normalizeMonthForInput(
                run.current_month
            );

            this.deviationThresholdPct =
            Number(
                run.deviation_threshold_pct || 50
            );

            this.notes =
            run.notes || "";

            this.control = {
            currentMonth:
                String(run.current_month || "").slice(0, 10),

            historyStart:
                String(run.history_start_date || "").slice(0, 10),

            historyEnd:
                String(run.history_end_date || "").slice(0, 10),

            historyWorkdays:
                Number(run.history_workdays || 0),

            priorYearStart:
                String(run.prior_year_start_date || "").slice(0, 10),

            priorYearEnd:
                String(run.prior_year_end_date || "").slice(0, 10),

            priorYearWorkdays:
                Number(run.prior_year_workdays || 0),

            deviationThresholdPct:
                Number(
                run.deviation_threshold_pct || 50
                ),

            months: [1, 2, 3].map((periodNumber) => {
                const period =
                periods.find(
                    (item) =>
                    Number(item.forecast_period) ===
                        periodNumber &&
                    String(item.loc || "")
                        .trim()
                        .toUpperCase() === "F"
                ) ||
                periods.find(
                    (item) =>
                    Number(item.forecast_period) ===
                    periodNumber
                );

                const start = period?.period_start_date
                ? String(period.period_start_date).slice(0, 10)
                : "";

                const end = period?.period_end_date
                ? String(period.period_end_date).slice(0, 10)
                : "";

                return {
                period: periodNumber,
                start,
                end,
                workdays: Number(period?.workdays || 0),

                label: start
                    ? new Date(
                        `${start}T00:00:00`
                    ).toLocaleString("en-US", {
                        month: "long",
                        year: "numeric"
                    })
                    : `Month ${periodNumber}`
                };
            })
            };

            const loadedMultipliers = {
            F: [1, 1, 1],
            D: [1, 1, 1],
            K: [1, 1, 1],
            L: [1, 1, 1]
            };

            for (const period of periods) {
            const loc = String(period.loc || "")
                .trim()
                .toUpperCase();

            const periodIndex =
                Number(period.forecast_period) - 1;

            if (
                loadedMultipliers[loc] &&
                periodIndex >= 0 &&
                periodIndex <= 2
            ) {
                loadedMultipliers[loc][periodIndex] =
                Number(period.multiplier || 1);
            }
            }

            this.multipliers = loadedMultipliers;

            this.rows = savedRows.map((row) =>
            this.normalizeSavedRow(row)
            );

            this.recalculateSummary();

            this.previewLoaded = true;
        } catch (error) {
            console.error(
            "load active Purchasing forecast error:",
            error
            );

            alert(
            error?.response?.data?.error ||
                error?.message ||
                "Failed to load the active Purchasing forecast."
            );
        } finally {
            this.loading = false;
        }
    },

    

    resetMultipliers() {
        for (const loc of this.locations) {
            this.multipliers[loc].splice(
            0,
            3,
            1,
            1,
            1
            );
        }

        this.previewStale = true;
    },

    copyMonthOneAcross() {
        for (const loc of this.locations) {
            const monthOne = Number(
            this.multipliers[loc][0] ?? 1
            );

            this.multipliers[loc].splice(
            1,
            2,
            monthOne,
            monthOne
            );
        }

        this.previewStale = true;
    },

    async handleMonthChange() {
      this.clearPreview();

      if (!this.forecastName) {
        this.forecastName =
          this.buildDefaultForecastName();
      }

      await this.loadControl(false);
    },

    async loadControl(showError = true) {
      this.loading = true;

      try {
        const params = {};

        if (this.currentMonth) {
          params.currentMonth =
            this.monthInputToDate(this.currentMonth);
        }

        params.threshold =
          this.deviationThresholdPct;

        const { data } = await axios.get(
          "/purchasing-forecast/control",
          {
            params
          }
        );

        this.control = data?.control || null;
        this.activeForecast = data?.active || null;

        if (this.control?.currentMonth) {
          this.currentMonth =
            this.normalizeMonthForInput(
              this.control.currentMonth
            );
        }

        if (
          this.control?.deviationThresholdPct !==
          undefined
        ) {
          this.deviationThresholdPct =
            Number(
              this.control.deviationThresholdPct
            );
        }

        if (!this.forecastName) {
          this.forecastName =
            this.buildDefaultForecastName();
        }
      } catch (error) {
        console.error(
          "load Purchasing forecast control error:",
          error
        );

        if (showError) {
          alert(
            error?.response?.data?.error ||
              "Failed to load Purchasing forecast controls."
          );
        }
      } finally {
        this.loading = false;
      }
    },

    buildPayload() {
      return {
        forecastName:
          this.forecastName ||
          this.buildDefaultForecastName(),

        currentMonth:
          this.monthInputToDate(
            this.currentMonth
          ),

        deviationThresholdPct:
          Number(
            this.deviationThresholdPct || 50
          ),

        notes: this.notes,

        multipliers: {
          F: this.multipliers.F.map(Number),
          D: this.multipliers.D.map(Number),
          K: this.multipliers.K.map(Number),
          L: this.multipliers.L.map(Number)
        },

        overrides: this.buildOverrides()
      };
    },

    buildOverrides() {
      return this.rows
        .filter((row) => row.hasOverride)
        .map((row) => ({
          itemNo: row.itemNo,
          loc: row.loc,

          month1OverrideQty:
            this.nullableNumber(
              row.month1.overrideQty
            ),

          month2OverrideQty:
            this.nullableNumber(
              row.month2.overrideQty
            ),

          month3OverrideQty:
            this.nullableNumber(
              row.month3.overrideQty
            ),

          reason:
            String(
              row.overrideReason || ""
            ).trim()
        }));
    },

    nullableNumber(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return null;
      }

      const number = Number(value);

      return Number.isFinite(number)
        ? number
        : null;
    },

    validateBeforePreview() {
      if (!this.currentMonth) {
        alert("Please select Month 1.");
        return false;
      }

      for (const loc of this.locations) {
        for (
          let index = 0;
          index < 3;
          index += 1
        ) {
          const value = Number(
            this.multipliers[loc][index]
          );

          if (
            !Number.isFinite(value) ||
            value < 0
          ) {
            alert(
              `Invalid multiplier for ${loc}, Month ${index + 1}.`
            );

            return false;
          }
        }
      }

      return true;
    },

    validateOverrides() {
      for (const row of this.rows) {
        if (
          row.hasOverride &&
          !String(
            row.overrideReason || ""
          ).trim()
        ) {
          alert(
            `Override reason is required for ${row.itemNo} at ${row.loc}.`
          );

          return false;
        }
      }

      return true;
    },

    async previewForecast() {
      if (!this.validateBeforePreview()) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await axios.post(
          "/purchasing-forecast/preview",
          this.buildPayload()
        );

        this.control = data?.control || null;

        this.rows = Array.isArray(data?.rows)
          ? data.rows.map(
              this.normalizePreviewRow
            )
          : [];

        this.summary =
          data?.summary || {
            itemLocationRows: 0,
            rawMaterialItems: 0,
            flaggedRows: 0,
            overriddenRows: 0,
            month1Total: 0,
            month2Total: 0,
            month3Total: 0
          };

        this.previewLoaded = true;
        this.previewStale = false;
      } catch (error) {
        console.error(
          "preview Purchasing forecast error:",
          error
        );

        alert(
          error?.response?.data?.error ||
            "Failed to calculate Purchasing forecast."
        );
      } finally {
        this.loading = false;
      }
    },

    normalizePreviewRow(row) {
      return {
        ...row,

        reviewFlags:
          Array.isArray(row.reviewFlags)
            ? [...row.reviewFlags]
            : [],

        month1: {
          ...row.month1,
          overrideQty:
            row.month1?.overrideQty ?? ""
        },

        month2: {
          ...row.month2,
          overrideQty:
            row.month2?.overrideQty ?? ""
        },

        month3: {
          ...row.month3,
          overrideQty:
            row.month3?.overrideQty ?? ""
        },

        overrideReason:
          row.overrideReason || "",

        hasOverride:
          Boolean(row.hasOverride)
      };
    },

    handleOverrideChange(row) {
      const overrideValues = [
        this.nullableNumber(
          row.month1.overrideQty
        ),
        this.nullableNumber(
          row.month2.overrideQty
        ),
        this.nullableNumber(
          row.month3.overrideQty
        )
      ];

      row.hasOverride =
        overrideValues.some(
          (value) => value !== null
        );

      row.month1.finalQty =
        overrideValues[0] === null
          ? Number(
              row.month1.calculatedQty || 0
            )
          : overrideValues[0];

      row.month2.finalQty =
        overrideValues[1] === null
          ? Number(
              row.month2.calculatedQty || 0
            )
          : overrideValues[1];

      row.month3.finalQty =
        overrideValues[2] === null
          ? Number(
              row.month3.calculatedQty || 0
            )
          : overrideValues[2];

      const totalWorkdays =
        this.periods.reduce(
          (sum, period) =>
            sum +
            Number(period.workdays || 0),
          0
        );

      const totalQuantity =
        Number(row.month1.finalQty || 0) +
        Number(row.month2.finalQty || 0) +
        Number(row.month3.finalQty || 0);

      row.averageDaily =
        totalWorkdays > 0
          ? totalQuantity / totalWorkdays
          : 0;

      row.averageMonthly =
        totalQuantity / 3;

      const manualFlagIndex =
        row.reviewFlags.indexOf(
          "MANUAL_OVERRIDE"
        );

      if (
        row.hasOverride &&
        manualFlagIndex === -1
      ) {
        row.reviewFlags.push(
          "MANUAL_OVERRIDE"
        );
      }

      if (
        !row.hasOverride &&
        manualFlagIndex !== -1
      ) {
        row.reviewFlags.splice(
          manualFlagIndex,
          1
        );

        row.overrideReason = "";
      }

      this.recalculateSummary();
    },

    recalculateSummary() {
      const summary = {
        itemLocationRows: this.rows.length,

        rawMaterialItems:
          new Set(
            this.rows.map(
              (row) => row.itemNo
            )
          ).size,

        flaggedRows: 0,
        overriddenRows: 0,

        month1Total: 0,
        month2Total: 0,
        month3Total: 0
      };

      for (const row of this.rows) {
        if (row.reviewFlags.length) {
          summary.flaggedRows += 1;
        }

        if (row.hasOverride) {
          summary.overriddenRows += 1;
        }

        summary.month1Total += Number(
          row.month1.finalQty || 0
        );

        summary.month2Total += Number(
          row.month2.finalQty || 0
        );

        summary.month3Total += Number(
          row.month3.finalQty || 0
        );
      }

      this.summary = summary;
    },

    async saveForecast() {
      if (!this.previewLoaded) {
        return;
      }

      if (!this.validateOverrides()) {
        return;
      }

      const confirmed = window.confirm(
        "Save and activate this Purchasing forecast? The current active forecast will be superseded."
      );

      if (!confirmed) {
        return;
      }

      this.saving = true;

      try {
        const payload = {
          ...this.buildPayload(),
          activate: true
        };

        const { data } = await axios.post(
          "/purchasing-forecast/runs",
          payload
        );

        alert(
          `Purchasing forecast ${data.forecastRunId} was saved and activated.`
        );

        await this.loadControl(false);

        this.previewLoaded = false;
        this.rows = [];
        this.resetSummary();
      } catch (error) {
        console.error(
          "save Purchasing forecast error:",
          error
        );

        alert(
          error?.response?.data?.error ||
            "Failed to save Purchasing forecast."
        );
      } finally {
        this.saving = false;
      }
    }
  },

  mounted() {
    this.loadControl();
  }
};
</script>

<style scoped>
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  margin-top: 20px;
  background-color: #0b0101;
  min-height: 100vh;
  padding-bottom: 40px;
  text-align: left !important;
}

.page-heading {
  font-family: "Cormorant Garamond", serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

.forecast-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.forecast-layout {
  display: grid;
  grid-template-columns: 205px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  color: #fff;
  margin-top: 80px;
}

.forecast-wrapper {
  width: 100%;
  min-width: 0;
}

.plan-it-btn,
.small-btn {
  border-radius: 10px;
  padding: 8px 16px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  font-family: inherit;
}

.plan-it-btn {
  width: 145px;
}

.small-btn {
  width: auto;
  padding: 6px 12px;
}

.plan-it-btn:hover:not(:disabled),
.small-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled,
.small-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.save-btn {
  background: #d9ead3;
  border-color: #93c47d;
}

.control-panel,
.table-panel,
.filter-panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
  margin-bottom: 12px;
}

.control-grid {
  display: grid;
  grid-template-columns:
    minmax(220px, 1.5fr)
    minmax(150px, 0.8fr)
    minmax(150px, 0.8fr)
    minmax(240px, 1.5fr);
  gap: 12px;
}

.control-field,
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.control-field label,
.filter-field label {
  font-weight: bold;
  font-size: 14px;
}

.control-field input,
.filter-field input,
.filter-field select,
.multiplier-input,
.override-input,
.reason-input {
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid #6b7280;
  background: #f9fafb;
  color: #111827;
  font-family: inherit;
  padding: 7px 8px;
}

.control-field input,
.filter-field input,
.filter-field select {
  width: 100%;
}

.date-summary-grid {
  display: grid;
  grid-template-columns: repeat(
    5,
    minmax(130px, 1fr)
  );
  gap: 10px;
  margin-top: 12px;
}

.date-card {
  border: 1px solid #555;
  border-radius: 8px;
  background: #1f2937;
  padding: 8px 10px;
  text-align: center;
}

.date-label {
  font-size: 12px;
  opacity: 0.8;
}

.date-value {
  margin-top: 3px;
  font-size: 16px;
  font-weight: bold;
  color: #e5e7eb;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background: rgb(67, 67, 67);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 100px;
  flex: 1 1 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 15px;
  opacity: 0.8;
  line-height: 1.1;
}

.summary-value {
  font-size: 22px;
  font-weight: bold;
  color: #cbbfbf;
  line-height: 1.1;
  margin-top: 2px;
}

.warning-value {
  color: #ffd166;
}

.override-value {
  color: #ffe599;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
}

.panel-title {
  font-size: 18px;
  font-weight: bold;
  color: #e5e7eb;
}

.table-scroll {
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #333;
}

.multiplier-scroll {
  max-height: 310px;
}

.detail-scroll {
  max-height: 72vh;
}

.forecast-table {
  width: 100%;
  border-collapse: collapse;
}

.multiplier-table {
  min-width: 760px;
}

.detail-table {
  min-width: 2450px;
}

.forecast-table th,
.forecast-table td {
  border: 1px solid #333;
  padding: 7px 8px;
  font-size: 14px;
  text-align: right;
  white-space: nowrap;
}

.forecast-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 2;
}

.detail-table thead tr:nth-child(2) th {
  top: 32px;
}

.forecast-table tr:nth-child(even) td {
  background: #0f141c;
}

.forecast-table tr:nth-child(odd) td {
  background: #0b0101;
}

.forecast-table th:first-child,
.forecast-table td:first-child,
.period-cell {
  text-align: left;
}

.item-cell,
.location-cell,
.period-cell {
  font-weight: bold;
  color: #e5e7eb;
}

.multiplier-input {
  width: 95px;
  text-align: right;
}

.override-cell {
  background: #3a3107 !important;
}

.override-input {
  width: 90px;
  text-align: right;
  background: #fff2cc;
  border-color: #d6b656;
}

.final-cell {
  font-weight: bold;
  color: #f5d76e;
}

.reason-cell {
  min-width: 250px;
}

.reason-input {
  width: 240px;
}

.reason-input:disabled {
  background: #374151;
  color: #9ca3af;
}

.missing-reason {
  border: 2px solid #dc2626;
  background: #fee2e2;
}

.flagged-row td {
  border-top-color: #745f16;
  border-bottom-color: #745f16;
}

.overridden-row td {
  box-shadow:
    inset 0 1px 0 rgba(255, 209, 102, 0.18),
    inset 0 -1px 0 rgba(255, 209, 102, 0.18);
}

.flags-cell {
  white-space: normal !important;
  min-width: 175px;
  text-align: left !important;
}

.flag-pill {
  display: inline-block;
  padding: 2px 6px;
  margin: 1px 3px 1px 0;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #111827;
  background: #d1d5db;
}

.flag-high {
  background: #f6b26b;
}

.flag-low {
  background: #ffe599;
}

.flag-negative {
  background: #e06666;
  color: #fff;
}

.flag-missing {
  background: #b4a7d6;
}

.flag-override {
  background: #ffd966;
}

.positive-deviation {
  color: #f6b26b;
  font-weight: bold;
}

.negative-deviation {
  color: #ffe599;
  font-weight: bold;
}

.filter-panel {
  display: flex;
  align-items: end;
  gap: 16px;
  flex-wrap: wrap;
}

.search-field {
  width: 260px;
}

.filter-field:not(.search-field) {
  width: 160px;
}

.filter-check {
  display: flex;
  align-items: center;
  min-height: 34px;
}

.filter-check label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.filter-count {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.8;
  padding-bottom: 7px;
}

.side-help,
.warning-box,
.override-box,
.active-forecast-box {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px;
  background: rgb(67, 67, 67);
  font-size: 13px;
  line-height: 1.6;
  color: #cbbfbf;
}

.warning-box {
  background: #3a2d09;
  color: #ffd166;
  border-color: #9a7b16;
}

.override-box {
  background: #403408;
  color: #ffe599;
  border-color: #b7a23a;
}

.active-forecast-box {
  background: #172319;
  border-color: #638c67;
}

.box-title {
  font-weight: bold;
  color: #d9ead3;
}

.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.empty-row {
  text-align: center !important;
  padding: 18px !important;
  opacity: 0.75;
}

/* Period Multiplier modal overlay */
.shortage-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.shortage-modal {
  background-color: #0b0101;
  color: #cbbfbf;
  padding: 10px 20px;
  border-radius: 8px;
  max-width: 900px;
  width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  font-family: Garamond, serif;
  box-sizing: border-box;
}

.multiplier-modal {
  max-width: 850px;
}

.shortage-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #444;
  padding-bottom: 0.5rem;
}

.shortage-modal-header h3 {
  margin: 0;
  font-family: "Cormorant Garamond", Garamond, serif;
  font-size: 24px;
  color: #cbbfbf;
}

.shortage-modal-body {
  width: 100%;
}

.close-btn {
  border: 1px solid #888;
  border-radius: 6px;
  background: #1f2937;
  color: #e5e7eb;
  font-size: 18px;
  line-height: 1;
  padding: 5px 9px;
  cursor: pointer;
}

.close-btn:hover {
  background: #374151;
}

.multiplier-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.multiplier-modal-note {
  border: 1px solid #62551c;
  border-radius: 8px;
  background: #302809;
  color: #ffe599;
  padding: 8px 10px;
  margin-bottom: 10px;
  font-size: 13px;
}

.multiplier-modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.stale-preview-warning {
  border: 1px solid #b38f17;
  border-radius: 8px;
  background: #3a2d09;
  color: #ffd166;
  padding: 9px 12px;
  margin-bottom: 12px;
  font-size: 14px;
}

.multiplier-modal .multiplier-table {
  min-width: 700px;
}

.multiplier-modal .multiplier-scroll {
  max-height: 55vh;
}

@media (max-width: 1200px) {
  .control-grid {
    grid-template-columns: repeat(
      2,
      minmax(200px, 1fr)
    );
  }

  .date-summary-grid {
    grid-template-columns: repeat(
      3,
      minmax(130px, 1fr)
    );
  }
}

@media (max-width: 850px) {
  .forecast-layout {
    grid-template-columns: 1fr;
  }

  .left-side-section {
    margin-top: 0;
    margin-right: 0;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .side-help,
  .warning-box,
  .override-box,
  .active-forecast-box {
    max-width: 300px;
  }

  .control-grid,
  .date-summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>